import { getQuoteId, isLifeInsuranceQuote, isTraditionalLTCQuote } from '@/util/quoteTypeGuards';
import {
  Quote,
  markIllustrationAsDownloaded,
  postIllustrationRequestRequest,
  selectClient,
  selectHasActiveIllustrationRequest,
  selectIllustration,
  selectSessionAdvisor,
} from '@client/model';
import { downloadFile } from '@client/util/secureDownload';
import { buildIllustrationRequestPayload } from '@shared';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type SupportedQuote = Quote | TraditionalLTCQuote;

interface UseIllustrationDownloadFlowProps {
  quote: SupportedQuote;
  selectedPremium?: number;
  clientPartner?: Client | null;
  fundingSourceId?: string;
  clientPolicyApplicationId?: string;
}

export function useIllustrationDownloadFlow({
  quote,
  selectedPremium,
  clientPartner,
  fundingSourceId,
  clientPolicyApplicationId,
}: UseIllustrationDownloadFlowProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const advisor = useSelector(selectSessionAdvisor);
  const illustration = useSelector(selectIllustration);
  const hasActiveIllustrationRequest = useSelector(selectHasActiveIllustrationRequest);

  // Track if user initiated a download to trigger auto-download when PDF becomes available
  const userInitiatedDownload = useRef(false);
  const hasAutoDownloaded = useRef(false);

  const quoteId = getQuoteId(quote);

  const activeRequest = illustration.activeRequests?.[quoteId];
  const completedIllustration = illustration.completedIllustrations?.[quoteId];

  const currentPremium = isTraditionalLTCQuote(quote)
    ? quote.primaryModalPremium
    : selectedPremium || (isLifeInsuranceQuote(quote) ? quote.premiumAmount : 0);

  const isIllustrationForCurrentPremium = (illustrationPremium?: number) => {
    if (isTraditionalLTCQuote(quote)) {
      return true;
    }
    if (!illustrationPremium) {
      return isLifeInsuranceQuote(quote) && currentPremium === quote.premiumAmount;
    }
    return illustrationPremium === currentPremium;
  };

  // Check if we have a valid illustration for the current premium AND policy application
  const hasValidCompletedIllustration =
    completedIllustration?.pdfUrl &&
    isIllustrationForCurrentPremium(completedIllustration.premiumAmount) &&
    completedIllustration.clientPolicyApplicationId === clientPolicyApplicationId;

  const hasValidActiveIllustration =
    activeRequest?.pdfUrl && isIllustrationForCurrentPremium(activeRequest.premiumAmount);
  // Active requests don't need validation since they're transient

  const hasIllustrationPdf = hasValidCompletedIllustration || hasValidActiveIllustration;

  const isActiveStatus =
    activeRequest?.status === 'initiated' || activeRequest?.status === 'polling';
  const isCreatingIllustration =
    isActiveStatus && isIllustrationForCurrentPremium(activeRequest.premiumAmount);

  const isThisQuoteBeingCreated = isCreatingIllustration;
  const isAnotherQuoteBeingCreated = hasActiveIllustrationRequest && !isThisQuoteBeingCreated;

  const handleViewPdf = (event: React.MouseEvent) => {
    event.stopPropagation();

    // Get the PDF for the current premium amount
    let pdfUrl: string | null = null;
    let requestId: string | null = null;

    if (hasValidCompletedIllustration && completedIllustration) {
      pdfUrl = completedIllustration.pdfUrl;
      requestId = completedIllustration.requestId;
    } else if (hasValidActiveIllustration && activeRequest) {
      pdfUrl = activeRequest.pdfUrl;
      requestId = activeRequest.requestId;
    }

    if (pdfUrl && requestId) {
      window.open(pdfUrl, '_blank');
      dispatch(markIllustrationAsDownloaded({ requestId }));
    }
  };

  const handleDownloadPdf = async (event: React.MouseEvent) => {
    event.stopPropagation();

    let pdfUrl: string | null = null;
    let requestId: string | null = null;

    if (hasValidCompletedIllustration && completedIllustration) {
      pdfUrl = completedIllustration.pdfUrl;
      requestId = completedIllustration.requestId;
    } else if (hasValidActiveIllustration && activeRequest) {
      pdfUrl = activeRequest.pdfUrl;
      requestId = activeRequest.requestId;
    }

    if (pdfUrl && requestId) {
      const filename = `policy-illustration-${quoteId}.pdf`;
      const result = await downloadFile(pdfUrl, filename);

      if (result.success) {
        dispatch(markIllustrationAsDownloaded({ requestId }));
      } else {
        console.error('Failed to download PDF:', result.error);
      }
    }
  };

  const startQuoteGeneration = () => {
    if (!client?.intakeSurvey?.clientZipCode) {
      console.error('Client zipcode is not available');
      return;
    }

    try {
      const payload = buildIllustrationRequestPayload({
        quote,
        client,
        clientPartner,
        advisor,
        selectedPremium,
        fundingSourceId,
        clientPolicyApplicationId,
      });

      dispatch(postIllustrationRequestRequest(payload));
    } catch (error) {
      console.error('Failed to build illustration request payload:', error);
    }
  };

  // Simplified create illustration function for the download flow
  const handleCreateIllustration = () => {
    // If another quote is being created, don't start a new one
    if (isAnotherQuoteBeingCreated) {
      console.log('Another quote is already being created');
      return;
    }

    // Mark that user initiated a download so we can auto-download when PDF is ready
    userInitiatedDownload.current = true;
    hasAutoDownloaded.current = false;

    startQuoteGeneration();
  };

  // Auto-download PDF when it becomes available after user initiated download
  useEffect(() => {
    if (
      userInitiatedDownload.current &&
      hasIllustrationPdf &&
      !hasAutoDownloaded.current &&
      !isCreatingIllustration
    ) {
      let pdfUrl: string | null = null;
      let requestId: string | null = null;

      if (hasValidCompletedIllustration && completedIllustration) {
        pdfUrl = completedIllustration.pdfUrl;
        requestId = completedIllustration.requestId;
      } else if (hasValidActiveIllustration && activeRequest) {
        pdfUrl = activeRequest.pdfUrl;
        requestId = activeRequest.requestId;
      }

      if (pdfUrl && requestId && !completedIllustration?.downloaded) {
        console.log('Auto-downloading PDF:', pdfUrl);
        hasAutoDownloaded.current = true;

        // Auto-download the PDF
        fetch(pdfUrl)
          .then(response => response.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `policy-illustration-${quoteId}.pdf`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            console.log('PDF auto-download triggered');
          })
          .catch(error => {
            console.error('Failed to auto-download PDF:', error);
          });

        dispatch(markIllustrationAsDownloaded({ requestId }));
      }
    }
  }, [
    hasIllustrationPdf,
    isCreatingIllustration,
    hasValidCompletedIllustration,
    hasValidActiveIllustration,
    completedIllustration,
    activeRequest,
    quoteId,
    dispatch,
  ]);

  return {
    hasIllustrationPdf,
    isCreatingIllustration,
    isAnotherQuoteBeingCreated,
    handleViewPdf,
    handleDownloadPdf,
    handleCreateIllustration,
  };
}
